import React, { useState } from 'react';
// @ts-ignore
import calendarMatrix from 'calendar-matrix';
import Icon from './Icon';
import { useQueryStrings } from '../context/QueryStringContext';
import Button from './Button';
import { isoDate } from '../../../../../../../shared/lib/time';
import { useData } from '../context/DataContext';
import { useApp } from '../context/AppContext';

interface CalendarPropsBase {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

type CalendarProps = CalendarPropsBase & React.HTMLProps<HTMLDivElement>;

function Calendar(props: CalendarProps) {
  const { config } = useApp();
  const { data } = useData();
  const { qs } = useQueryStrings();

  const todayDate = new Date(config.date);
  const selectedDate = new Date(qs.date);

  const [viewingDate, setViewingDate] = useState(new Date(selectedDate));

  const daysMatrix = calendarMatrix(viewingDate);

  const years = Array.from({ length: 11 }, (_, i) => todayDate.getFullYear() + i);
  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString(qs.lang, { month: 'long' })
  );
  const daysOfWeek = Array.from({ length: 7 }, (_, i) =>
    new Date(0, 0, i).toLocaleString(qs.lang, { weekday: 'short' })
  );

  function handlePrevMonth() {
    const result = new Date(viewingDate);
    result.setMonth(viewingDate.getMonth() - 1);
    setViewingDate(result);
  }

  function handleNextMonth() {
    const result = new Date(viewingDate);
    result.setMonth(viewingDate.getMonth() + 1);
    setViewingDate(result);
  }

  function handleDayClick(day: number) {
    if (day < 0) return;
    props.onDateChange(new Date(viewingDate.getFullYear(), viewingDate.getMonth(), day));
  }

  function handleTodayClick() {
    props.onDateChange(todayDate);
  }

  function handleMonthChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newMonth = parseInt(e.target.value, 10);
    setViewingDate(new Date(viewingDate.getFullYear(), newMonth, 1));
  }

  function handleYearChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newYear = parseInt(e.target.value, 10);
    setViewingDate(new Date(newYear, viewingDate.getMonth(), 1));
  }

  function dayDisabled(date: Date) {
    const cutoffDate = new Date(todayDate);
    cutoffDate.setUTCDate(cutoffDate.getUTCDate() + data.appConfig.FUTURE_DAY_CAP);
    return isoDate(date) < isoDate(todayDate) || isoDate(date) > isoDate(cutoffDate);
  }

  return (
    <div
      className={`flex flex-col items-center p-4 mx-auto bg-white ${props.className}`}
      style={{ maxWidth: '600px' }}
    >
      <div className="flex w-full items-center mb-2">
        <div className="flex justify-between items-center w-full">
          <Button
            onClick={handlePrevMonth}
            className="calendar-active-text border-none px-3 py-2 text-xl"
          >
            <Icon icon={['fass', 'arrow-left']} />
          </Button>
          <select
            className="calendar-active-text text-right bg-transparent text-lg font-semibold mr-2"
            value={viewingDate.getMonth()}
            onChange={handleMonthChange}
          >
            {months.map((monthName, index) => (
              <option key={monthName} value={index} className="text-sm">
                {monthName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-between items-center w-full">
          <select
            className="calendar-active-text bg-transparent text-lg font-semibold"
            value={viewingDate.getFullYear()}
            onChange={handleYearChange}
          >
            {years.map(yr => (
              <option key={yr} value={yr} className="text-sm">
                {yr}
              </option>
            ))}
          </select>
          <Button
            onClick={handleNextMonth}
            className="calendar-active-text border-none px-3 py-2 text-xl"
          >
            <Icon icon={['fass', 'arrow-right']} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 w-full text-center">
        {daysOfWeek.map(day => (
          <div key={day} className="font-semibold py-2">
            {day}
          </div>
        ))}
        {daysMatrix.flat().map((day: number, index: number) => {
          const isSelected =
            selectedDate.getUTCDate() === day &&
            selectedDate.getUTCMonth() === viewingDate.getUTCMonth() &&
            selectedDate.getUTCFullYear() === viewingDate.getUTCFullYear();
          const isToday =
            todayDate.getUTCDate() === day &&
            todayDate.getUTCMonth() === viewingDate.getUTCMonth() &&
            todayDate.getUTCFullYear() === viewingDate.getUTCFullYear();

          const classes = ['grid items-center justify-center'];

          if (isSelected) {
            classes.push('calendar-active-bg font-semibold rounded-full text-white');
          } else if (isToday) {
            classes.push('calendar-active-text font-semibold rounded-full');
          } else if (day < 0) {
            classes.push('text-gray-300');
          }

          const thisDate = new Date(
            Date.UTC(viewingDate.getUTCFullYear(), viewingDate.getUTCMonth(), day)
          );

          return (
            <Button
              key={index}
              className={`aspect-square grid items-center justify-center ${
                dayDisabled(thisDate) ? 'text-gray-300' : ''
              }`}
              disabled={dayDisabled(thisDate)}
              onClick={() => handleDayClick(day)}
            >
              <div style={{ width: 40, height: 40 }} className={classes.join(' ')}>
                {day > 0 ? Math.abs(day) : ''}
              </div>
            </Button>
          );
        })}
      </div>

      <div className="w-full flex justify-start mt-4">
        <Button onClick={handleTodayClick} className="calendar-active-text font-semibold px-3 py-2">
          Today
        </Button>
      </div>
    </div>
  );
}

export default Calendar;
